import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { FC, ReactNode } from 'react';
import { createContext, useContext } from 'react';
import { MetaLabels } from 'src/components/data-display/meta-label';
import { Tooltip } from 'src/components/overlay/tooltip';
import { Hint } from 'src/components/text/hint';
import { cn } from 'src/utils/cn';

export type StatusProps = {
  variant: 'warning' | 'error' | 'success' | 'inactive' | 'ready' | 'info';
  status: ReactNode;
  tooltipInfo?: string;
  appendix?: ReactNode;
  /** Array of <a href="./?path=/docs/components-data-display-meta-label--docs">`MetaLabel`</a> items or a `ReactNode`. No need to wrap them using `MetaLabels`. */
  metaLabels?: ReactNode;
  inline?: boolean;
  'data-testid'?: string;
};

const variantColorClassMap: Record<StatusProps['variant'], string> = {
  warning: cn(`
    bg-background-support-warning-default
    legacy:bg-legacy-warning
  `),
  error: cn(`
    bg-background-support-error-default
    legacy:bg-legacy-error
  `),
  success: cn(`
    bg-background-support-success-default
    legacy:bg-legacy-success
  `),
  inactive: cn(
    `
      bg-background-support-inactive-default
      legacy:bg-legacy-inactive
    `
  ),
  ready: cn(`
    bg-background-support-ready-default
    legacy:bg-legacy-ready
  `),
  info: cn(`
    bg-background-support-info-default
    legacy:bg-legacy-ready
  `),
};

export const StatusDisplay: FC<StatusProps> = (props) => {
  const {
    variant,
    status,
    appendix,
    inline = false,
    metaLabels,
    tooltipInfo,
    ['data-testid']: testId = 'status-display',
  } = props;
  if (appendix && metaLabels) {
    throw new Error(
      'Expecting either appendix or metaLabels to be passed as prop, but can not include both together.'
    );
  }

  const contextMode = useStatusDisplayMode();

  const infoContent = (
    <>
      <span
        className={cn(
          contextMode === 'page' &&
            `
              font-body-semibold
              legacy:font-legacy-bold
            `
        )}
        data-testid={`${testId}-text`}
      >
        {status}

        {tooltipInfo && (
          <>
            {' '}
            <Tooltip content={tooltipInfo}>
              {(targetProps) => (
                <FontAwesomeIcon icon={faInfoCircle} {...targetProps} />
              )}
            </Tooltip>
          </>
        )}
      </span>

      {appendix && (
        <Hint
          data-testid={`${testId}-appendix`}
          mode={contextMode === 'page' ? 'default' : 'small'}
        >
          {appendix}
        </Hint>
      )}
      {metaLabels && <MetaLabels>{metaLabels}</MetaLabels>}
    </>
  );

  return (
    <div
      className={`
        grid grid-flow-col items-baseline justify-start gap-sm
        legacy:gap-[0.6rem]
      `}
      data-testid={testId}
    >
      <span
        className={cn(
          `
            size-[calc(var(--spacing-2xl)/2.2)] rounded-full leading-none
            legacy:size-[10.8px]
          `,
          contextMode === 'page' &&
            `
              size-[calc(var(--spacing-2xl)/1.9)] translate-y-icon-offset
              legacy:size-[12.71px]
            `,
          variantColorClassMap[variant]
        )}
        data-variant={variant}
        data-testid={`${testId}-icon`}
        aria-hidden
      />

      <div
        className={cn(`grid`, inline && `grid-flow-col items-baseline gap-sm`)}
        data-testid={`${testId}-info`}
      >
        {infoContent}
      </div>
    </div>
  );
};

// Provider
type StatusDisplayMode = 'page' | 'standalone';

const StatusDisplayContext = createContext<StatusDisplayMode>('standalone');

export function useStatusDisplayMode() {
  return useContext(StatusDisplayContext);
}

export const StatusDisplayProvider = StatusDisplayContext.Provider;
